import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { DialogTrigger } from '@radix-ui/react-dialog'

import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { showToast } from '@jotta/ui/ToastProvider'

import { SelectAlbumDialog } from '../Album/Dialogs/SelectAlbumDialog'

import type { PhotoActionHandlerActionProps } from './PhotoActionHandlers'
import { useDispatchPhotoActionMutation } from './useDispatchPhotoActionMutation'
const debug = Debug('jotta:photos:action:AddPhotosToAlbum')

export const ActionAddPhotosToAlbum = observer<
  PhotoActionHandlerActionProps<'ADD_PHOTOS_TO_ALBUM'>
>(function ActionAddPhotosToAlbum({
  action,
  photoIds = [],
  albumId,
  addPhotoMode,
  albums = [],
  dispatch,
  onActionDialogClose,
  buttonClass,
  disabled = false,
}) {
  const { t } = useLingui()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const mutation = useDispatchPhotoActionMutation(
    'ADD_PHOTOS_TO_ALBUM',
    {
      onSettled: async albumPromise => {
        const album = await albumPromise
        const count = album?.photos ? album.photos.length : album?.total
        const addedPhotoText = plural(count ?? 0, {
          one: 'Added 1 photo',
          other: 'Added # photos',
        })

        debug('onSettled', album)

        if (addPhotoMode) {
          setOpen(false)
          showToast({
            message: addedPhotoText,
            hasCloseButton: true,
          })
          navigate(`/share/${addPhotoMode}`, { replace: true })
          return
        }
        if (album) {
          debug('Displaying alert')
          setOpen(false)
          if (onActionDialogClose) {
            onActionDialogClose('ADD_PHOTOS_TO_ALBUM')
          }
          showToast({
            message: addedPhotoText,
            hasCloseButton: true,
            actionButton: {
              label: t`Show album`,
              onClick: () => {
                navigate(`/photo/album/${album.id}`)
              },
            },
          })
          return
        }
        if (onActionDialogClose) {
          onActionDialogClose('ADD_PHOTOS_TO_ALBUM')
        }
        setOpen(false)
      },
    },
    dispatch,
  )
  if (addPhotoMode) {
    return (
      <Button
        className={buttonClass}
        aria-label={action.label}
        data-testid={action.testid}
        aria-busy={mutation.isPending}
        disabled={disabled}
        key={action.actionType}
        icon={action.icon}
        onClick={() => mutation.mutateAsync([addPhotoMode, photoIds])}
      >
        {action.label}
      </Button>
    )
  }
  return (
    <SelectAlbumDialog
      open={open}
      onClose={open => {
        setOpen(open)
        if (!open && onActionDialogClose) {
          onActionDialogClose(action.actionType)
        }
      }}
      currentAlbumId={albumId}
      addToAlbumHandler={selectedAlbumId => {
        debug('addToAlbumHandler', selectedAlbumId)
        if (selectedAlbumId) {
          mutation.mutateAsync([selectedAlbumId, photoIds])
        }
        if (onActionDialogClose) {
          onActionDialogClose(action.actionType)
        }
        setOpen(false)
      }}
      triggerElement={
        <DialogTrigger
          aria-label={action.label}
          data-testid={action.testid}
          aria-busy={mutation.isPending}
          disabled={disabled}
          key={action.actionType}
          className={buttonClass}
        >
          <PlainBrandIcon icon={action.icon} />
          <span>{action.label}</span>
        </DialogTrigger>
      }
      dispatch={dispatch}
      albums={albums}
    />
  )
})
